import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import * as DocumentPicker from 'expo-document-picker';
import { DocumentResult } from 'expo-document-picker';

import * as ImagePicker from 'expo-image-picker';

import * as Permissions from 'expo-permissions';
import { useEffect, useState } from 'react';
import { ActionSheetIOS, ImagePickerResult, Platform } from 'react-native';

const WHITE_LISTED_MIME_TYPES = ['image/*', 'application/pdf'];

interface UseFileUploaderParams {
  onValueChange: (document: undefined | DocumentResult | ImagePickerResult) => void;
  uploadURL: string;
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
  type?: string | string[]; // MIME type
}

export const useFileUploader = ({
  type = WHITE_LISTED_MIME_TYPES,
  uploadURL,
  onSuccess,
  onError,
  onValueChange
}: UseFileUploaderParams) => {
  const [selectedDocument, setSelectedDocument] = useState<undefined | DocumentResult | ImagePickerResult>(undefined);
  const [status, setStatus] = useState<'NOT_SELECTED' | 'UPLOADING' | 'SUCCESS' | 'ERROR'>('UPLOADING');

  const handlePress = () => {
    switch (Platform.OS) {
      case 'ios':
        ActionSheetIOS.showActionSheetWithOptions(
          {
            options: ['Cancel', 'Take a photo', 'Choose from library'],
            cancelButtonIndex: 0,
            userInterfaceStyle: 'dark'
          },
          (buttonIndex) => {
            if (buttonIndex === 0) {
              // cancel action
            } else if (buttonIndex === 1) {
              takeAPhoto();
            } else if (buttonIndex === 2) {
              chooseFromLibrary();
            }
          }
        );
        break;
      default:
        chooseFromLibrary();
    }
  };

  const takeAPhoto = async () => {
    // Check if the user has granted permission to access the camera and photo library
    const { status } = await Permissions.askAsync(Permissions.CAMERA_ROLL, Permissions.CAMERA);
    if (status !== 'granted') {
      // If permission is not granted, display an error message
      console.error('Permission to access camera and photo library was denied');
      return;
    }

    try {
      const file = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.All,
        allowsEditing: true,
        quality: 1
      });
      setSelectedDocument(file);
      // upload the file to the given uploadURL and call onSuccess or onError
      uploadFileMutation.mutate({ file });
      return file;
    } catch (error) {
      console.log(error);
    }
  };

  const chooseFromLibrary = async () => {
    try {
      const file = await DocumentPicker.getDocumentAsync({
        type
      });
      setSelectedDocument(file);
      uploadFileMutation.mutate({ file });
      return file;
    } catch (error) {
      console.log(error);
    }
  };

  const uploadFile = async ({ file }: { file: any }) => {
    try {
      if (file.type === 'cancel') return;
      setStatus('UPLOADING');
      const response = await fetch(file.uri);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append('file', blob);
      formData.append('fileName', file.name || `NEW_FILE_${Date.now()}`);
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const { data } = await axios.post(uploadURL, formData, config);
      setStatus('SUCCESS');
      onSuccess(data);
      return data;
    } catch (error) {
      setStatus('ERROR');
      onError(error);
      console.log(error);
      throw error;
    }
  };

  const uploadFileMutation = useMutation(uploadFile);

  useEffect(() => {
    onValueChange(selectedDocument);
    return () => {
      onValueChange(undefined);
    };
  }, [selectedDocument]);

  return {
    status,
    selectedDocument,
    takeAPhoto,
    handlePress
  };
};
